import * as React from 'react';

import {
	Field,
	FieldProps,
	Form,
	Formik,
} from 'formik';
import moment from 'moment';

import { Select } from '@app/components/Selects/Select';
import { Option } from '@app/components/Selects/Option';

import { Icon } from '@app/image/icon';
import { ActionMode, Button, ButtonVariant } from '@app/components/Button/Button';
import { SEARCH_TEXT_PACKAGE_TAB } from '@app/vendor/texts';

import { request } from '@app/services/api';
import { CruisePackages, PackageFilterOption } from '@app/objects/CruisePackages';
import { Nullable } from '@app/objects/Utility';

import '@app/scss/layout.scss';

interface FormValues {
	cruiseLine: Nullable<number>;
	date: Nullable<number>;
	destination: Nullable<number>;
}

const initialValues: FormValues = {
	cruiseLine: null,
	date: null,
	destination: null,
};

const baseUrl = '/searchengine/ovc_exclusives.cfm';
const goSearch = (values: FormValues, filters: Nullable<CruisePackages>) => {
	const params: Array<string> = [];

	if (values.cruiseLine) {
		const id = values.cruiseLine - 1;
		const cruiseLine = filters?.cruiseLine.find((item) => item.id === id);
		params.push(`SortSupplier=${cruiseLine?.name}`);
	}

	if (values.destination) {
		const destination = filters?.destination.find((item) => item.id === values.destination);
		params.push(`SortDestination=${destination?.name}`);
	}

	if (values.date) params.push(`sortDeparture=${moment(values.date).format('M/YYYY')}`);

	const path = `${baseUrl}?${params.join('&')}`;
	window.open(path);
};

function sorted(list?: Array<PackageFilterOption>) {
	return list?.sort((a, b) => {
		if (a.name > b.name) return 1;

		return -1;
	});
}

export const SearchPackages: React.FC = () => {
	const [filters, setFilters] = React.useState<Nullable<CruisePackages>>(null);
	const [loading, setLoading] = React.useState<boolean>(false);

	React.useEffect(() => {
		setLoading(true);
		request('api/filters/v1/packages')
			.then((item: CruisePackages) => setFilters(item))
			.finally(() => setLoading(false));
	}, []);

	return (
		<Formik
			initialValues={initialValues}
			onSubmit={(values: FormValues) => goSearch(values, filters)}
		>
			{(formikBag) => (
				<Form id="package-search-form">
					<div className="layout-container">
						<div className="col-12 col-lg-6 col-xl-12">
							<div className="layout-container">
								<div className="text-size_large col-12 col-xl-6">
									<div className="font-family_primary font-weight_semi-bold">
										SAVE $1,000s
									</div>
									<div>
										<i className="font-family_decorative font-weight_regular">
											with Online Vacation Center's Exclusives
										</i>
									</div>
								</div>
								<div className="text-size_extra-small font-weight_regular col-12 col-xl-6" style={{ lineHeight: '21px' }}>
									{SEARCH_TEXT_PACKAGE_TAB}
								</div>
							</div>
						</div>

						<div className="col-12 col-lg-6 col-xl-12">
							<div className="layout-container content-halign_center">
								<Field name="destination">
									{({ field, form }: FieldProps<number, FormValues>) => (
										<div className="col-12 col-xl-4">
											<Select<number>
												placeholder="Destination/River"
												Icon={Icon.Location}
												loading={loading}
												value={field.value}
												onChange={(value?: number) => {
													form.setFieldValue(field.name, value);
												}}
											>
												{sorted(filters?.destination)?.map((item: PackageFilterOption) => (
													<Option key={item.id} value={item.id}>
														{item.name}
													</Option>
												))}
											</Select>
										</div>
									)}
								</Field>
								<Field name="cruiseLine">
									{({ field, form }: FieldProps<number, FormValues>) => (
										<div className="col-12 col-xl-4">
											<Select
												placeholder="Cruise Line/Tour Operator"
												Icon={Icon.Helm}
												loading={loading}
												value={field.value}
												onChange={(value?: number) => {
													form.setFieldValue(field.name, value);
												}}
											>
												{filters?.cruiseLine.map((item: PackageFilterOption) => (
													<Option key={item.id + 1} value={item.id + 1}>
														{item.name}
													</Option>
												))}
											</Select>
										</div>
									)}
								</Field>
								<Field name="date">
									{({ field, form }: FieldProps<number, FormValues>) => (
										<div className="col-12 col-xl-4">
											<Select
												placeholder="Travel Dates"
												Icon={Icon.Calendar}
												loading={loading}
												value={field.value}
												onChange={(value?: number) => {
													form.setFieldValue(field.name, value);
												}}
											>
												{filters?.date.map((item: number) => (
													<Option key={item} value={item}>
														{moment(item).format('MMMM YYYY')}
													</Option>
												))}
											</Select>
										</div>
									)}
								</Field>

								<div className="button-container col-12">
									<div className="layout-container reverse content-valign_top content-halign_center">
										<div className="col-12 col-xl-4">
											<Button
												className="uppercase"
												variant={ButtonVariant.Filled}
												onClick={formikBag.handleSubmit}
											>
												Search Packages
											</Button>
										</div>
										<div className="col-12 col-xl-4 btn-reset">
											<Button
												variant={ButtonVariant.Underlined}
												className="uppercase"
												action={ActionMode.Button}
												onClick={formikBag.resetForm}
											>
												Reset all
											</Button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</Form>
			)}
		</Formik>
	);
};
